import type { I18nDictionaryShape } from '../../types';
import type { featuresEnGb } from '../en-GB/features';

export const featuresEsEs = {
  annotations: {
    aiFeedbackAlert: {
      desktopPrompt: 'Haz clic derecho en la foto para dar feedback a la IA',
      improveAccuracy: 'Mejorar la precisión.',
      improveAccuracyFor: 'Mejorar la precisión para {{tradeCompanyName}}.',
      mobilePrompt: 'Mantén pulsada la foto para dar feedback a la IA',
    },
    aiFeedbackSwitch: {
      label: 'Mostrar feedback de la IA',
    },
    aiFilters: {
      analysisFailedDescription:
        'Nuestro equipo lo investigará. Mientras tanto, continúa con las fotos restantes del informe.',
      analysisFailedTitle: 'La IA no pudo analizar esta foto',
      description: 'Muestra u oculta los contornos del material y compáralos con la foto original.',
      noFiltersDescription:
        'La IA de Safi solo delimita material no objetivo (como impurezas o elementos adheridos). Esta foto contiene solo material objetivo (p. ej., UBC o PET), por lo que no hay contornos ni filtros que mostrar.',
      noFiltersTitle: 'No hay filtros que mostrar porque se detectó un 100% de material objetivo',
      showOutlines: 'Mostrar contornos de la IA',
      title: 'Ajustar filtros de IA',
    },
    commentBubble: {
      actions: {
        deleteFeedback: 'Eliminar feedback',
        edit: 'Editar',
      },
      adminMeta: 'ID:{{commentId}} IA:{{aiVersion}}',
      deleteDialog: {
        confirm: 'Eliminar',
        message: '¿Seguro que quieres eliminar este feedback? Esta acción no se puede deshacer.',
        title: 'Eliminar feedback',
      },
    },
    commentFormDrawer: {
      actions: {
        cancel: 'Cancelar',
        save: 'Guardar',
      },
      feedbackType: {
        incorrectDetection: 'Objeto detectado incorrectamente',
        missingDetection: 'Objeto no detectado',
      },
      labels: {
        note: 'Nota',
      },
      titleCreate: 'Dar feedback a la IA',
      titleEdit: 'Editar feedback de la IA',
    },
    helpAndFeedbackBar: {
      downloadFullImageAriaLabel: 'Descargar imagen completa de la evidencia',
    },
    segmentationOverlay: {
      ariaLabel: 'Imagen con superposición de segmentación',
      helpText: {
        desktop: 'Pasa el cursor sobre la foto para ampliar',
        mobile: 'Amplía con los dedos',
      },
    },
    speechBubbleAvatar: {
      viewCommentAriaLabel: 'Ver comentario de {{fullName}}',
    },
  },
  auth: {
    companySignUpForm: {
      actions: {
        next: 'Siguiente',
      },
      labels: {
        companyType: 'Tipo de empresa',
        country: 'País',
        name: 'Nombre',
      },
      notifications: {
        errorSigningUp: 'Error al registrarse',
      },
      validation: {
        companyTypeRequired: 'El tipo de empresa es obligatorio',
        countryRequired: 'El país es obligatorio',
        nameRequired: 'El nombre es obligatorio',
      },
    },
    otpForm: {
      actions: {
        signIn: 'Iniciar sesión',
      },
      labels: {
        passcode: 'Código',
      },
      validation: {
        digitsOnly: 'Debe contener solo dígitos',
        passcodeLength: 'El código de un solo uso debe tener 4 dígitos',
        passcodeRequired: 'El código de un solo uso es obligatorio',
      },
    },
    signInEmailForm: {
      actions: {
        next: 'Siguiente',
      },
      labels: {
        email: 'Correo electrónico',
      },
      links: {
        signInWithPhone: 'Iniciar sesión con número de teléfono',
      },
      validation: {
        emailRequired: 'El correo electrónico es obligatorio',
        invalidEmail: 'Correo electrónico no válido',
        termsRequired: 'Debes aceptar los términos y condiciones',
      },
    },
    signInPage: {
      subtitle:
        'Inicia sesión en {{brandName}} con tu correo electrónico o número de teléfono del trabajo. Te enviaremos un código de un solo uso para acceder más rápido.',
      title: 'Control de calidad con confianza',
    },
    signInPhoneForm: {
      actions: {
        next: 'Siguiente',
        signInWithEmail: 'Iniciar sesión con correo electrónico',
      },
      labels: {
        phoneNumber: 'Número de teléfono',
      },
      validation: {
        phoneNumberRequired: 'El número de teléfono es obligatorio',
      },
    },
    termsAndPolicyAgreement: {
      fullSentence:
        'Acepto los <termsLink>Términos y condiciones</termsLink> y la <privacyLink>Política de privacidad</privacyLink> al unirme a {{brandName}}.',
    },
    userSignUpForm: {
      actions: {
        next: 'Siguiente',
      },
      labels: {
        email: 'Correo electrónico',
        fullName: 'Nombre completo',
        phoneNumber: 'Número de teléfono',
      },
      notifications: {
        errorSigningUp: 'Error al registrarse',
      },
      validation: {
        emailRequired: 'El correo electrónico es obligatorio',
        fullNameRequired: 'El nombre completo es obligatorio',
        invalidEmail: 'Correo electrónico no válido',
        phoneNumberRequired: 'El número de teléfono es obligatorio',
        termsRequired: 'Debes aceptar los términos y condiciones',
      },
    },
  },
  backlog: {
    breakdowns: {
      evaluatedSpecsItems: {
        ungroupedMaterials: 'Materiales sin agrupar',
      },
      formatDirtScore: {
        average: 'Promedio',
        clean: 'Limpio',
        dirty: 'Sucio',
        notAvailable: 'N/D',
        veryClean: 'Muy limpio',
      },
      materialAiBreakdown: {
        aiModelNotSupported: 'Modelo de IA no compatible',
      },
      materialLabels: {
        aluminium: 'Aluminio',
        aluminiumAerosol: 'Aerosol de aluminio',
        aluminiumFoodCan: 'Lata de aluminio para alimentos',
        brown: 'Marrón',
        clear: 'Transparente',
        clearNonFoodGrade: 'Transparente, no apto para alimentos',
        clearPetTray: 'Bandeja PET transparente',
        petTrayContaminatedClear: 'Bandeja PET transparente contaminada',
        darkBlue: 'Azul oscuro',
        dirtyBottles: 'Botellas sucias',
        expandedPolystyrene: 'Poliestireno expandido',
        film: 'Film',
        foilAndTray: 'Lámina y bandeja',
        fullSleeve: 'Manga completa',
        green: 'Verde',
        hdpe: 'HDPE',
        hdpeJazz: 'jazz',
        hdpeNaturalFoodGrade: 'Natural alimentario',
        hdpeNaturalNonFoodGrade: 'Natural no alimentario',
        hdpeSiliconTubes: 'Tubos de silicona',
        hdpeWhiteFoodGrade: 'blanco alimentario',
        hdpeWhiteNonFoodGrade: 'blanco no alimentario',
        lightBlue: 'Azul claro',
        metal: 'Metal',
        nonAluminium: 'No aluminio',
        nonHdpe: 'No HDPE',
        nonMetal: 'No metálico',
        nonPet: 'No PET',
        nonPlastic: 'No plástico',
        oilBottle: 'Botella de aceite',
        other: 'Otros',
        otherAluminium: 'Otro aluminio',
        otherColour: 'Otro color',
        otherMetal: 'Otro metal',
        otherPlastic: 'Otro plástico',
        pet: 'PET',
        polypropylene: 'PP',
        prohibitive: 'Prohibitivo',
        total: 'Total',
        ubc: 'UBC',
        waste: 'Residuo',
        white: 'Blanco',
      },
      representativeEvidenceFilter: {
        label: 'Ver imágenes de:',
        options: {
          bestBales: 'Mejores balas',
          typicalBales: 'Balas típicas',
          worstBales: 'Peores balas',
        },
      },
      representativeEvidenceSection: {
        distributionTitle: 'Distribución de {{targetName}}',
      },
      specificationAlerts: {
        groupDescription:
          '<boldAction>{{actionText}}</boldAction> - El grupo <boldName>{{name}}</boldName> está <boldComparison>{{comparison}}</boldComparison>',
        groupDescriptionWithoutComparison:
          '<boldAction>{{actionText}}</boldAction> - El grupo <boldName>{{name}}</boldName> está',
        groupIs: 'el grupo está',
        the: '- El',
        title: 'Alertas',
      },
      totalWeightWarning: {
        message:
          '¡Advertencia! El peso total ({{formattedTotalWeight}}kg) no coincide con la suma de los pesos del desglose ({{formattedSum}}kg).',
      },
    },
    cameraGuidance: {
      useCameraStream: {
        notifications: {
          cannotUseCamera: 'No se puede usar la cámara',
          refreshAndAllowCamera:
            'Actualiza esta página e inténtalo de nuevo. Permite el acceso a la cámara cuando se solicite.',
        },
      },
    },
    common: {
      noData: 'Sin datos',
    },
    dealDetails: {
      fields: {
        destination: 'Destino',
        endProcessor: 'Procesador final',
        inspections: 'Inspecciones',
        loadingSite: 'Sitio de carga',
        loads: 'Cargas',
        material: 'Material',
        producer: 'Productor',
        risk: 'Riesgo',
      },
      header: {
        deal: 'Acuerdo {{dealId}}',
      },
    },
    dealRevenueDetails: {
      columns: {
        description: 'Descripción',
        id: 'ID',
        invoiceStatus: 'Estado de factura',
        invoiceXeroAmountPaid: 'Importe pagado en Xero',
        invoiceXeroFullyPaidOn: 'Fecha de pago completo en Xero',
        invoiceXeroId: 'ID de factura en Xero',
        lineTotal: 'Total de línea',
        quantity: 'Cantidad',
        unitAmount: 'Importe unitario',
      },
    },
    documentGeneration: {
      notifications: {
        failedToSaveFile: 'Error al guardar el archivo',
        failedToUploadFile: 'Error al subir el archivo',
      },
    },
    evidence: {
      baleEvidenceGallery: {
        analysingMultiplePhotos: 'Analizando {{count}} fotos...',
        analysingSinglePhoto: 'Analizando {{count}} foto...',
        noEvidenceFound: 'No se encontraron fotos',
      },
      bulkDownload: {
        empty: 'No hay evidencia para descargar',
        error: 'Error al descargar la evidencia',
        inProgress: 'Preparando descarga de evidencia…',
        partialSuccess: '{{downloaded}} de {{total}} fotos descargadas',
        success: '{{count}} fotos descargadas',
      },
      common: {
        editSample: 'Editar muestra',
        manageInspection: 'Gestionar inspección',
      },
      evidenceAiDetails: {
        aiAnalysisTitle: 'Análisis IA',
        aiImagesTitle: 'Imágenes IA',
      },
      evidenceAiLooseSessionDetails: {
        aiLooseAnalysisTitle: 'Análisis IA suelta',
        sampleImagesTitle: 'Imágenes de muestra',
      },
      evidencePhotosViewer: {
        bulkDeleteError: 'No se pudieron eliminar las fotos',
        bulkDeleteSuccess: 'Se eliminaron {{count}} fotos correctamente',
      },
      loadingPhotoEvidenceGallery: {
        noOtherImagesAvailable: 'No hay otras imágenes disponibles.',
      },
      manualBreakdownFormFields: {
        kilogramUnit: 'kg',
      },
      sessionEditDialog: {
        actions: {
          editDeleteEvidence: 'Editar / Eliminar evidencia',
        },
      },
      sessionEvidenceEditDialog: {
        step1Title: 'Paso 1. Selecciona la evidencia que quieres modificar',
        step2Title: 'Paso 2. Indica cómo modificar la evidencia seleccionada',
      },
      sessionSummary: {
        anonymous: 'Anónimo',
        withOrigin: '{{origin}} - Subido por {{uploadedBy}}',
        withoutOrigin: 'Subido por {{uploadedBy}}',
      },
      uploadMenu: {
        downloadAllEvidence: 'Descargar toda la evidencia',
        downloadingAllEvidence: 'Descargando…',
        editEvidence: 'Editar evidencia',
        menuAriaLabel: 'Más acciones',
        offlineModeComingSoon: 'Modo sin conexión próximamente',
        openCamera: 'Abrir cámara',
        uploadEvidence: 'Subir evidencia',
      },
      uploadOptionsButton: {
        actions: {
          upload: 'Subir',
        },
        options: {
          openCamera: 'Abrir cámara',
          uploadPhoto: 'Subir foto',
        },
      },
    },
    evidenceGrid: {
      columns: {
        evidence: 'Evidencia',
      },
    },
    evidenceUploadButton: {
      label: 'Subir',
      notifications: {
        failedToUploadEvidence: 'Error al subir la evidencia',
        failedToUploadPhoto: 'Error al subir {{photoName}}: {{error}}',
        successfullyUploadedEvidence: 'Evidencia subida correctamente',
      },
    },
    externalLoad: {
      common: {
        supplier: 'Proveedor',
      },
    },
    findTicketsTable: {
      columns: {
        category: 'Categoría',
        status: 'Estado',
        summary: 'Resumen',
        team: 'Equipo',
      },
    },
    firmQuoteBulkForm: {
      columns: {
        bunkerAdjustmentFactorCost: 'Coste de factor de ajuste por combustible',
        bunkerAdjustmentFactorCurrency: 'Moneda de factor de ajuste por combustible',
        carrier: 'Transportista',
        containerType: 'Tipo de contenedor',
        demandIncoterm: 'Incoterm de demanda',
        destinationCountry: 'País de destino',
        destinationLat: 'Latitud de destino',
        destinationLon: 'Longitud de destino',
        destinationName: 'Nombre de destino',
        directLandCost: 'Coste de transporte terrestre directo',
        directLandCurrency: 'Moneda de transporte terrestre directo',
        e2eCost: 'Coste E2E',
        e2eCurrency: 'Moneda E2E',
        estimatedDeparture: 'Salida estimada',
        expiresOn: 'Caduca el',
        forwarder: 'Transitario',
        notes: 'Notas',
        oceanFreightCost: 'Coste de flete marítimo',
        oceanFreightCurrency: 'Moneda de flete marítimo',
        onCarriageCost: 'Coste de transporte posterior',
        onCarriageCurrency: 'Moneda de transporte posterior',
        originCountry: 'País de origen',
        originLat: 'Latitud de origen',
        originLon: 'Longitud de origen',
        originName: 'Nombre de origen',
        peakSeasonSurchargeCost: 'Coste de recargo por temporada alta',
        peakSeasonSurchargeCurrency: 'Moneda de recargo por temporada alta',
        portOfDischarge: 'POD',
        portOfLoading: 'POL',
        preCarriageCost: 'Coste de pretransporte',
        preCarriageCurrency: 'Moneda de pretransporte',
        quotedOn: 'Cotizado el',
        supplyIncoterm: 'Incoterm de oferta',
        typeOfGoods: 'Tipo de mercancía',
      },
    },
    grade: {
      compositionReport: {
        annotatedBaleAlt: 'Bala anotada',
      },
      imageQualityPrompt: {
        label: 'Volver a tomar',
      },
    },
    groundControl: {
      common: {
        descriptions: {
          calculateDealProfitability: 'Calcular la rentabilidad del acuerdo',
          createPurchaseOrderAndSendToSeller: 'Crear una orden de compra y enviarla al vendedor para su firma',
          createSalesOrderAndSendToBuyer: 'Crear una orden de venta y enviarla al comprador para su firma',
          viewEditAndMatch: 'Ver, editar y relacionar',
        },
        labels: {
          createPurchaseOrder: 'Crear orden de compra',
          createSalesOrder: 'Crear orden de venta',
          dealProfitabilityCalculator: 'Calculadora de rentabilidad del acuerdo',
          viewPurchaseOrders: 'Ver órdenes de compra',
          viewSalesOrders: 'Ver órdenes de venta',
        },
      },
      tabs: {
        advanced: {
          label: 'Avanzado',
          links: {
            manageMaterialProducts: {
              description: 'Ver y editar productos de material',
              label: 'Gestionar productos de material',
            },
            pernType: {
              description: 'Actualizar tipos PERN',
              label: 'Tipo PERN',
            },
          },
        },
        contracts: {
          label: 'Contratos',
        },
        deals: {
          label: 'Acuerdos',
          links: {
            goToDealHub: {
              description: 'Ver y gestionar tareas del acuerdo',
              label: 'Ir a DealHub',
            },
            viewAllDeals: {
              description: 'Ver y editar todos los acuerdos y cargas',
              label: 'Ver todos los acuerdos',
            },
          },
        },
        finance: {
          label: 'Finanzas',
          links: {
            createShippingRate: {
              description: 'Crear tarifa de transporte y todos sus detalles asociados',
              label: 'Crear tarifa de transporte',
            },
            manageBanks: {
              description: 'Ver y editar datos bancarios',
              label: 'Gestionar bancos',
            },
            manageCosts: {
              description: 'Crear, editar y ver costes',
              label: 'Gestionar costes',
            },
            manageExternalCompanies: {
              description: 'Crear, editar y ver datos de empresas externas',
              label: 'Gestionar empresas externas',
            },
            manageFinancing: {
              description: 'Enviar solicitudes de pago a Finmid',
              label: 'Gestionar financiación',
            },
            manageRevenue: {
              description: 'Crear, editar y ver ingresos',
              label: 'Gestionar ingresos',
            },
            marketPernPrices: {
              description: 'Ver precios históricos de PERN desde T2E',
              label: 'Precios de mercado PERN',
            },
          },
        },
        inspections: {
          label: 'Inspecciones',
          links: {
            addExternalEvidence: {
              description: 'Añadir evidencia de fuentes externas',
              label: 'Añadir evidencia externa',
            },
            addSiteEvidence: {
              description: 'Añadir nueva evidencia del sitio',
              label: 'Añadir evidencia del sitio',
            },
            createInspection: {
              description: 'Crear una nueva inspección',
              label: 'Crear inspección',
            },
            inspectionCompanies: {
              description: 'Ver y editar empresas de inspección',
              label: 'Empresas de inspección',
            },
            inspections: {
              description: 'Ver y editar inspecciones',
              label: 'Inspecciones',
            },
            inspectors: {
              description: 'Ver y editar inspectores',
              label: 'Inspectores',
            },
            viewUpcomingLoads: {
              description: 'Ver y editar próximas cargas',
              label: 'Ver próximas cargas',
            },
          },
        },
        support: {
          label: 'Soporte',
          links: {
            getSupport: {
              description: 'Crear una incidencia de soporte en Jira',
              label: 'Obtener soporte',
            },
            helpAndLearn: {
              description: 'Guías de ayuda',
              label: 'Ayuda y aprendizaje',
            },
          },
        },
        trade: {
          label: 'Comercio',
          links: {
            createDemandSignal: {
              description: 'Crear una señal de demanda. Se sincronizará con HubSpot',
              label: 'Crear señal de demanda',
            },
            createSupplySignal: {
              description: 'Crear una señal de oferta. Se sincronizará con HubSpot',
              label: 'Crear señal de oferta',
            },
            generateDocuments: {
              description: 'Generar documentos para órdenes de compra, órdenes de venta, envíos, etc.',
              label: 'Generar documentos',
            },
            qualityReport: {
              description: 'Añadir datos e imágenes del informe de calidad',
              label: 'Informe de calidad',
            },
            transportMetabase: {
              description: 'Enlace a Metabase de transporte',
              label: 'Metabase de transporte',
            },
          },
        },
        transport: {
          label: 'Transporte',
          links: {
            manageCarriers: {
              description: 'Crear, editar y ver transportistas',
              label: 'Gestionar transportistas',
            },
            syncTransportCosts: {
              description: 'Sincronizar costes de transporte desde una hoja de cálculo',
              label: 'Sincronizar costes de transporte',
            },
            viewPorts: {
              description: 'Ver todos los puertos',
              label: 'Ver puertos',
            },
          },
        },
        usersAndCompanies: {
          label: 'Usuarios y empresas',
          links: {
            copySessions: {
              description: 'Copiar sesiones en ML Accuracy Testing desde una carga CSV',
              label: 'Copiar sesiones',
            },
            createUserAndCompany: {
              description: 'Crear un usuario y una nueva empresa, o invitarlo a una empresa existente',
              label: 'Crear usuario y empresa',
            },
            hijackUser: {
              description: 'Acceder rápidamente a la cuenta de un usuario',
              label: 'Suplantar a un usuario',
            },
            staffOnboarding: {
              description: 'Crear un nuevo usuario administrador y hacerlo miembro de Safi',
              label: 'Incorporación de personal',
            },
            viewAllUsers: {
              description: 'Ver todos los usuarios en HubSpot',
              label: 'Ver todos los usuarios',
            },
            viewTradeCompanies: {
              description: 'Ver y editar empresas comerciales de oferta y demanda',
              label: 'Ver todas las empresas comerciales',
            },
          },
        },
      },
    },
    guidePriceChip: {
      label: 'Precio guía',
      unavailable: 'Precio guía no disponible',
    },
    helpGuides: {
      aiBale: {
        partOne: {
          boldText: 'Mantente a 1 metro máximo',
          imageAlt: 'Mantente a 1 metro como máximo',
          trailingText: 'frente al montón.',
        },
      },
      aiLoose: {
        partFour: {
          and: 'y',
          clearPile: 'Retira el montón',
          imageAlt: 'Retira el montón',
          repeatSteps: 'repite los pasos 1-3',
          untilText: 'hasta que hayas tomado una foto por montón de toda la muestra.',
        },
        partOne: {
          imageAlt: 'Haz un montón',
          makePile: 'Haz un montón',
          middleText: 'de tu muestra y extiéndelo uniformemente en el suelo.',
          takePhoto: 'Haz 1 foto por montón.',
        },
        partThree: {
          boldIntro: 'Revisa la foto antes de apartar tu montón.',
          imageAlt: 'Revisar y eliminar',
          trailingText: 'Si no es correcta, puedes eliminarla y hacer una nueva.',
          warningBold: 'solo 1 foto por montón',
          warningPrefix: 'Sube',
          warningSuffix: 'para evitar sesgar los resultados de IA.',
        },
      },
      common: {
        completeAlt: 'Completo',
      },
      data: {
        guides: {
          aiBale: 'IA bala',
          aiBriquette: 'IA briqueta',
          aiLoose: 'IA suelta',
        },
        sections: {
          qualityControlGuides: 'Guías de control de calidad',
        },
        subcategories: {
          howToTakeGreatAiPhotos: 'Cómo tomar excelentes fotos para IA',
        },
      },
    },
    inspectionMutate: {
      cancellationReasons: {
        dealCancelled: 'Acuerdo cancelado',
        missedLoading: 'Carga perdida',
        noCoverage: 'Sin cobertura',
        noInspector: 'Sin inspector',
        notNeeded: 'No necesario',
        other: 'Otro',
        siteRefused: 'El sitio rechazó',
      },
      inspectionResults: {
        failed: 'Fallida',
        passed: 'Aprobada',
        pending: 'Pendiente',
      },
      inspectionStatuses: {
        backlog: 'Pendiente',
        booked: 'Reservada',
        cancelled: 'Cancelada',
        complete: 'Completada',
        planning: 'Planificación',
        scheduled: 'Programada',
      },
      inspectionTypes: {
        pern: 'Pern',
        sirim: 'Sirim',
        standard: 'Estándar',
        supplier: 'Proveedor',
      },
    },
    inspectionsList: {
      columns: {
        company: 'Empresa',
        deal: 'Acuerdo',
        endProcessor: 'Procesador final',
        inspector: 'Inspector',
        loads: 'Cargas',
        material: 'Material',
        producer: 'Productor',
        risk: 'Riesgo',
        slug: 'Slug',
        status: 'Estado',
        time: 'Hora',
      },
      values: {
        notAvailable: 'N/D',
      },
    },
    loadDetails: {
      actions: {
        notNeeded: 'No necesaria',
      },
      fields: {
        collectionTime: 'Hora de recogida',
        inspection: 'Inspección',
        status: 'Estado',
      },
      values: {
        hasInspection: 'Tiene inspección',
        noInspection: 'Sin inspección',
      },
    },
    loadsList: {
      columns: {
        collectionTime: 'Hora de recogida',
        deal: 'Acuerdo',
        destination: 'Destino',
        endProcessor: 'Procesador final',
        hasInspection: 'Tiene inspección',
        load: 'Carga',
        loadingSite: 'Sitio de carga',
        material: 'Material',
        producer: 'Productor',
        risk: 'Riesgo',
        status: 'Estado',
      },
      values: {
        no: 'No',
        notAvailable: 'N/D',
        yes: 'Sí',
      },
    },
    manageDataButton: {
      buttonLabel: 'Gestionar datos',
      options: {
        aiBalePhotos: 'Fotos IA de balas',
        loadingEvidence: 'Evidencia de carga',
        marketingMedia: 'Material de marketing',
        samples: 'Muestras',
        siteEvidence: 'Evidencia del sitio',
        specifications: 'Especificaciones',
      },
    },
    materialCategoryActions: {
      notifications: {
        cannotSaveMaterial: 'No se puede guardar el material',
        errorCreatingMaterial: 'Error al crear el material',
        errorDeletingMaterial: 'Error al eliminar el material',
        errorSavingMaterial: 'Error al guardar el material',
        savedSuccessfully: 'Guardado correctamente',
        selectedTargetGroupHasNoOperands:
          'El grupo objetivo seleccionado no tiene operandos. Elige otro grupo objetivo.',
        skippedSpecifications: 'No se guardaron {{count}} especificación(es) porque no tienen operandos asignados.',
      },
    },
    materialComponents: {
      supportedComponentList: {
        buttonText: 'Añadir componente del material',
        title: 'Componentes del material',
      },
    },
    materialDirtLevel: {
      options: {
        clean: 'Limpio',
        dirty: 'Sucio',
        notAvailable: 'N/D',
        veryClean: 'Muy limpio',
      },
    },
    materialProducts: {
      validation: {
        atLeastOneBuyerMaterialComponentIsRequired:
          'Se requiere al menos un valor de componente de material del comprador.',
        atLeastOneSellerMaterialComponentIsRequired:
          'Se requiere al menos un valor de componente de material del vendedor.',
        buyerAndSellerMaterialComponentCountsMustMatch:
          'El número de valores de componentes de material del comprador y del vendedor debe ser el mismo.',
      },
    },
    materialProductsList: {
      columns: {
        buyerMaterialComponents: 'Componentes del material del comprador',
        capsAndLabels: 'Tapones y etiquetas',
        country: 'País',
        foodGrade: 'Apto para alimentos',
        id: 'ID',
        materialGrade: 'Calidad del material',
        materialOrigin: 'Origen del material',
        materialSpec: 'Especificación del material',
        process: 'Proceso',
        sellerMaterialComponents: 'Componentes del material del vendedor',
      },
      values: {
        no: 'No',
        notSpecified: 'N/D',
        yes: 'Sí',
      },
    },
    notificationParty: {
      label: 'Parte notificada',
      options: {
        allViaAgent: 'Todas las comunicaciones a través del agente',
        directWithBuyer: 'Comunicarse directamente con el comprador',
      },
    },
    orders: {
      validation: {
        fixedPriceAndDateMustBeProvidedTogether: 'El precio fijo y la fecha fija deben proporcionarse juntos',
      },
    },
    paymentMethod: {
      label: 'Método de pago',
      options: {
        documentAgainstPayment: 'Documentos contra pago',
        telegraphicTransfer: 'Transferencia telegráfica',
      },
    },
    percentageLightBlue: {
      label: 'Porcentaje de azul claro',
      options: {
        notAvailable: 'N/D',
      },
    },
    qualityReport: {
      evidenceTabs: {
        aiBaleAnalysis: 'Análisis IA de balas',
        aiLooseAnalysis: 'Análisis IA suelta',
        loading: 'Carga',
        manualWeightSample: 'Muestra de peso manual',
        marketing: 'Marketing',
        materialSpecification: 'Especificación de material',
        site: 'Sitio',
      },
      siteMaterialQualityReportHeader: {
        evidencePhotoAlt: 'Foto de evidencia',
      },
    },
    qualityReportSummary: {
      titles: {
        availableMaterial: 'Material disponible',
        deliveryLocation: 'Lugar de entrega',
        estimatedDelivery: 'Entrega estimada',
        location: 'Ubicación',
        material: 'Material',
        materialGrade: 'Calidad del material',
        tonnesPerLoad: 'Toneladas por carga',
      },
    },
    siteMaterials: {
      notifications: {
        errorCreatingMaterial: 'Error al crear el material',
        materialCreated: 'Material creado',
        productLinkedSuccessfully: 'Producto vinculado correctamente',
      },
    },
    timeFilter: {
      options: {
        last30Days: 'Últimos 30 días',
        last7Days: 'Últimos 7 días',
        last90Days: 'Últimos 90 días',
        next14Days: 'Próximos 14 días',
        next2Days: 'Próximos 2 días',
        next7Days: 'Próximos 7 días',
      },
    },
    variablePricingFields: {
      indexOptions: {
        lmeAluminium: 'Aluminio LME',
        lmeAluminiumMidwestPremium: 'Aluminio LME + Prima Midwest',
      },
    },
  },
  breakdowns: {
    adminAiVersionTitle: {
      value: 'IA:{{version}}',
    },
    aiSampleBreakdown: {
      analysisNotAvailable: 'Análisis no disponible',
    },
    aluMaterialSampleBreakdown: {
      labels: {
        aluminium: 'Aluminio',
        foilAndTray: 'Lámina y bandeja',
        foodCan: 'Lata para alimentos',
        nonAluminium: 'No aluminio',
        nonMetal: 'No metálico',
        otherAluminium: 'Otro aluminio',
        otherMetal: 'Otro metal',
        total: 'Total',
        ubc: 'UBC',
      },
    },
    cleanliness: {
      label: 'Limpieza',
      tooltip:
        'La limpieza es una medida de lo limpio que está el material. Va desde muy limpio, limpio, medio y sucio. La suciedad incluye grasa, manchas y polvo presentes en la superficie del material.',
    },
    evaluatedSpecsBreakdown: {
      customizeTableFor: 'Personalizar la tabla para',
      targetChip: 'Objetivo',
      thisMaterial: 'este material',
      title: 'Análisis de IA',
    },
    noAnalysisAlert: {
      description:
        'La IA no pudo analizar las fotos. Puedes revisar el motivo en cada foto, eliminar las rechazadas y volver a intentarlo.',
      guidance:
        'Consulta la <guideLink>guía {{guideLabel}}</guideLink> para obtener consejos sobre cómo tomar mejores fotos.',
      noSubmittedPhotosDescription:
        'Aún no se han enviado fotos para el análisis. Añade fotos para iniciar el análisis.',
      title: 'No hay resultados disponibles',
    },
    analysisInProgressAlert: {
      pendingTitle: 'Análisis en curso',
      pendingDescription:
        '{{pendingCount}} de {{submittedCount}} fotos todavía se están analizando. Los resultados deberían aparecer en unos minutos.',
      partialTitle: 'Se están analizando más fotos',
      partialDescription:
        'Los resultados de abajo se basan en {{acceptedCount}} de {{submittedCount}} fotos. Todavía se están analizando {{pendingCount}} más.',
      rejectedDescription: '{{rejectedCount}} fotos fueron rechazadas. Revisa el motivo en cada foto.',
    },
    petFlake: {
      fields: {
        bulkDensity: 'Densidad aparente',
        clearFlakes: 'Escamas transparentes',
        colouredFlakes: 'Escamas de color',
        darkBlueFlakes: 'Escamas azul oscuro',
        ferrousMetal: 'Metal ferroso',
        glue: 'Pegamento',
        greenFlakes: 'Escamas verdes',
        intrinsicViscosity: 'Viscosidad intrínseca',
        isophthalicAcid: 'Ácido isoftálico',
        lightBlueFlakes: 'Escamas azul claro',
        meltFlowRate: 'Índice de fluidez (MF) (260 °C/2,16 kg)',
        meltingPoint: 'Punto de fusión',
        nonFerrousMetal: 'Metal no ferroso',
        otherImpurities: 'Otras impurezas',
        paper: 'Papel',
        particlesSizeBetween10And12mm: 'Partículas de entre 10 y 12 mm',
        particlesSizeBetween5And10mm: 'Partículas de entre 5 y 10 mm',
        particlesSizeLt5mm: 'Partículas de tamaño < 5 mm',
        petContent: 'Contenido de tereftalato de polietileno (PET)',
        ph: 'pH',
        polyolefinContamination: 'Contaminación por poliolefinas',
        pvcContamination: 'Contaminación por PVC',
        residualContamination: 'Contaminación residual',
        residualMoisture: 'Humedad residual',
        sampledBy: 'Muestreado por',
        woodenMaterials: 'Materiales de madera',
        yellowFlakes: 'Escamas amarillas',
      },
    },
    petMaterialSampleBreakdown: {
      labels: {
        clear: 'Transparente',
        clearNonFoodGrade: 'Transparente, no apto para alimentos',
        clearPetTray: 'Bandeja PET transparente',
        darkBlue: 'Azul oscuro',
        film: 'Film',
        green: 'Verde',
        largeClear5L: 'Transparente grande (≥5L)',
        lightBlue: 'Azul claro',
        liquid: 'Líquido',
        nonPetBottle: 'Botella no PET',
        nonPlastic: 'No plástico',
        other: 'Otro',
        otherPlastic: 'Otro plástico',
        petBottle: 'Botella PET',
        prohibitive: 'Prohibitivo',
        total: 'Total',
      },
    },
    taintTaborMaterialSampleBreakdown: {
      labels: {
        aluIron: 'Alu./hierro',
        aluNonMetal: 'Alu./no metálico',
        aluWithAttachments: 'Alu. con adherencias',
        aluminium: 'Aluminio',
        cast: 'Fundición',
        castIron: 'Fundición/hierro',
        ferrousMetal: 'Metal ferroso',
        finesLt20mm: 'Finos <20 mm',
        nonAluminium: 'No aluminio',
        nonMetal: 'No metálico',
        otherMetal: 'Otro metal',
        prohibitive: 'Prohibitivo',
        radiatorCondenser: 'Radiador/condensador',
        stainlessSteel: 'Acero inoxidable',
        taintTabor: 'Taint Tabor',
        total: 'Total',
        ubcFoil: 'UBC/lámina',
      },
    },
    zorbaMaterialSampleBreakdown: {
      labels: {
        aluminiumUbc: 'Aluminio - UBC',
        compositeMaterials: 'Materiales compuestos',
        copperBrassRedsYellows: "Cobre y latón - 'rojos y amarillos'",
        finesLt20mm: 'Finos <20 mm',
        nonFerrousMetalWhites: "Metal no ferroso - 'blancos'",
        nonTarget: 'No objetivo',
        otherMetalFerrous: 'Otro metal (ferroso)',
        otherNonMetal: 'Otro no metálico',
        postConsumerAluminiumPackaging: 'Envases de aluminio posconsumo',
        prohibitive: 'Prohibitivo',
        targetMaterial: 'Material objetivo',
        total: 'Total',
      },
    },
  },
  cameraGuidance: {
    camera: {
      notifications: {
        autoCaptureOff: 'Captura automática desactivada',
        autoCaptureOn: 'Captura automática activada',
      },
      rejectedPhotos: {
        message: 'Toca la foto de abajo para ver el motivo del error, luego elimina la foto y vuelve a intentarlo',
        photo: 'foto',
        photos: 'fotos',
        title: '{{prefix}} al revisar {{count}} {{countLabel}}!',
        titlePrefixObfuscated: 'Error',
        titlePrefixWithAi: 'La IA no pudo',
      },
    },
    cameraFooter: {
      actions: {
        capture: 'Capturar',
        complete: 'Completar',
        guide: 'Guía',
      },
      tooltips: {
        takePhotoToComplete: 'Haz una foto para completar',
      },
    },
    cameraHeader: {
      autoCapture: 'Captura automática',
      flashAriaLabel: 'Alternar flash',
      confirmationDialog: {
        actions: {
          complete: 'Completar',
          exitAnyway: 'Salir de todos modos',
        },
        messageWithAiLabel:
          'Tus fotos se guardarán, pero no verás los resultados de la IA de inmediato. Para ver ahora el informe de IA, pulsa Completar.',
        messageWithoutAiLabel:
          'Tus fotos se guardarán, pero no verás los resultados de inmediato. Para ver ahora el informe, pulsa Completar.',
        title: '¿Salir de la cámara?',
      },
      settings: {
        description: 'Usa el modo sin conexión si tu conexión de datos es débil para evitar perder fotos',
        title: 'Ajustes',
      },
    },
    countdownOverlay: {
      snap: '¡Foto!',
    },
    previewImageReview: {
      actions: {
        deleteAndRetake: 'Eliminar y repetir',
      },
      evidencePhotoAlt: 'Foto de evidencia',
    },
  },
  charts: {
    targetBaleWeightsHistogram: {
      axes: {
        x: 'Contenido objetivo previsto %',
        y: 'Número de balas',
      },
      labels: {
        average: 'Promedio: {{targetGrade}}%',
        bales: 'Balas',
      },
      tooltip: {
        label: '% detectado: {{label}}',
        value: '{{value}} balas',
      },
    },
  },
  dataCards: {
    aiLooseAnalysis: {
      description:
        'Un proceso de muestreo simplificado con pesos estimados por la IA de Safi. Los valores se redondean a 0,01 kg.',
      latestInspectionTitle: 'Última inspección',
      showMediaLabel: 'Mostrar contenido multimedia',
      title: 'Análisis IA de material suelto',
    },
    aluminiumComposition: {
      reportTitle: 'Informe',
      targetDistributionTitle: 'Distribución objetivo',
    },
    common: {
      loads: 'Cargas',
      metricTonnes: 'Toneladas métricas',
    },
    hdpeComposition: {
      aiBaleAnalysisTitle: 'Análisis IA de balas',
      reportTitle: 'Informe',
    },
    hdpeCompositionQualityReport: {
      actions: {
        showMedia: 'Mostrar contenido multimedia',
      },
      description:
        '<bold>Análisis de peso de HDPE</bold> que muestra el porcentaje de material detectado en una serie de balas inspeccionadas históricamente por inspectores de Safi.',
      fields: {
        latestInspection: 'Última inspección',
        supplierBalesAnalysed: 'Balas del proveedor analizadas',
      },
      title: 'Análisis IA de balas',
    },
    loadingDetails: {
      actions: {
        showMedia: 'Mostrar contenido multimedia',
      },
      fields: {
        loadingNotes: 'Notas de carga',
        loadingTechnique: 'Técnica de carga',
        packedOn: 'Empacado el',
        targetPayload: 'Carga objetivo',
      },
      title: 'Detalles de carga',
      values: {
        targetPayloadMt: '{{targetPayload}} (MT)',
      },
    },
    materialNotAnalysed: {
      analysisTitle: 'Análisis',
      noResultsDescription:
        'Todavía no hemos desarrollado un modelo para este tipo específico de material. Pero con más datos, podremos ofrecer análisis en el futuro.',
      noResultsTitle: 'No hay resultados disponibles',
      reportTitle: 'Informe',
    },
    materialSpecifications: {
      actions: {
        showMedia: 'Mostrar contenido multimedia',
      },
      analysis: {
        completed: 'Completado',
        notCompleted: 'No completado',
      },
      fields: {
        baleDimensions: 'Dimensiones de la bala',
        capsAndLabels: 'Tapones y etiquetas',
        density: 'Densidad (kg/m³)',
        ferrousCheck: 'Comprobación ferrosa',
        foodGrade: 'Apto para alimentos',
        materialAnalysisNotes: 'Notas del análisis del material',
        materialForm: 'Forma del material',
        materialProcess: 'Proceso del material',
        origin: 'Origen',
        radiationCheck: 'Comprobación de radiación',
        strapping: 'Flejado',
        visualCheck: 'Inspección visual',
      },
      title: 'Especificaciones del material',
      units: {
        meters: 'm',
      },
      values: {
        no: 'No',
        yes: 'Sí',
      },
    },
    petComposition: {
      aiBaleAnalysisTitle: 'Análisis IA de balas',
      reportTitle: 'Informe',
    },
    petCompositionQualityReport: {
      actions: {
        showMedia: 'Mostrar contenido multimedia',
      },
      description:
        '<bold>Análisis de peso de PET</bold> que muestra el porcentaje de material detectado en una serie de balas inspeccionadas históricamente por inspectores de Safi.',
      fields: {
        latestInspection: 'Última inspección',
        supplierBalesAnalysed: 'Balas del proveedor analizadas',
      },
      title: 'Análisis IA de balas',
    },
    site: {
      actions: {
        showMedia: 'Mostrar contenido multimedia',
      },
      fields: {
        machinesUsed: 'Máquinas utilizadas',
        materialOrigin: 'Origen del material',
        monthlyVolume: 'Volumen mensual',
        siteGround: 'Suelo del sitio',
        siteNotes: 'Notas del sitio',
        storageAndCover: 'Almacenamiento y cobertura',
      },
      monthlyVolumeValue: '{{productionVolume}} (MT)',
      title: 'Sitio',
    },
    targetBaleWeights: {
      actions: {
        showMedia: 'Mostrar contenido multimedia',
      },
      attachmentDetails: {
        foodCan: 'Lata para alimentos',
        nonMetal: 'No metálico',
        otherAluminium: 'Otro aluminio',
        otherMetal: 'Otro metal',
        title: 'Detalles de adherencias',
      },
      callouts: {
        attachmentWeightContent: 'Contenido en peso de adherencias',
        averageUbcWeightContent: 'Contenido medio en peso de UBC',
      },
      evidencePhotoAlt: 'Foto de evidencia',
      inspectorsGatheredMessage:
        'Los inspectores han recopilado datos al analizar <bold>{{count}}</bold> balas entre <bold>{{from}}</bold> y <bold>{{to}}</bold>',
      inspectorsGatheredPrefix: 'Los inspectores han recopilado datos al analizar',
      showBaleQualityData: 'Mostrar datos de calidad de las balas',
      tabs: {
        bestBales: 'Mejores balas',
        typicalBales: 'Balas típicas',
        ubcSuffix: 'UBC',
      },
      title: 'Análisis IA de balas',
    },
    tradeCompanySessionLoose: {
      aiLooseAnalysisTitle: 'Análisis IA de material suelto',
      reportTitle: 'Informe',
      sampleTotalWeightTitle: 'Peso total de la muestra',
    },
    transactionHistory: {
      alerts: {
        newSupplier: 'Nuevo proveedor. No has comprado antes a este proveedor.',
        purchasedBefore: 'Ya has comprado antes a este proveedor.',
      },
      title: 'Historial de transacciones',
      total: {
        description: 'Transacciones entre este proveedor y otros compradores en los últimos 6 meses.',
      },
    },
    weightSampleAnalysis: {
      actions: {
        showMedia: 'Mostrar contenido multimedia',
      },
      description: {
        withTotalWeight:
          'Los datos se basan en la muestra manual más reciente de {{totalWeightInKg}} del material de salida del proveedor, realizada por un inspector de Safi. Los valores se redondean a 0,01 kg.',
        withoutTotalWeight: 'Los valores se redondean a 0,01 kg.',
      },
      labels: {
        latestInspection: 'Última inspección',
      },
      title: 'Análisis manual de la muestra',
    },
  },
  evidence: {
    actionBar: {
      deletePrompt: '¿Seguro que quieres eliminar la evidencia seleccionada?',
      deleteTitle: 'Eliminar {{evidence}}',
      selectedCount: '{{selected}} seleccionadas',
    },
    chips: {
      ai: 'IA',
      selfServe: 'Auto-servicio',
    },
    defaultBreakdownFormFields: {
      actions: {
        addBreakdownItem: 'Agregar elemento de desglose',
      },
      labels: {
        name: 'Nombre',
        unit: 'Unidad',
        value: 'Valor',
      },
      title: 'Desglose',
    },
    editForm: {
      actions: {
        delete: 'Eliminar',
        edit: 'Editar',
      },
      labels: {
        newEvidenceType: 'Nuevo tipo de {{evidence}}',
        newLabel: 'Nuevo',
        typeLabel: 'Tipo',
      },
      notifications: {
        deleteError: 'Error al eliminar la evidencia',
        deleteSuccess: 'Evidencia eliminada',
        updateError: 'Error al actualizar la evidencia',
        updateSuccess: 'Evidencia actualizada',
      },
      validation: {
        typeOrSiteMaterialRequired: 'Debes indicar el tipo de evidencia o el material del sitio',
      },
      warnings: {
        deleteMessage:
          'Estás a punto de eliminar esta evidencia de este material del sitio. Esto la eliminará del informe de calidad asociado. Confirma para continuar.',
        editMessage:
          'Estás a punto de cambiar esta evidencia. Esto la eliminará del informe de calidad asociado. Confirma para continuar.',
        title: '¡Advertencia!',
      },
    },
    inspectionSessionDetails: {
      labels: {
        dealId: 'ID del acuerdo:',
        inspectionSlug: 'Slug de inspección:',
      },
      tooltips: {
        reportAvailableOnCompletion: 'Una vez completada la inspección, aquí encontrarás un enlace al informe.',
        searchInDrive: 'Buscar inspección en Google Drive',
        viewDealDetails: 'Ver detalles del acuerdo',
        viewExternalReport: 'Ver informe externo',
      },
    },
    offlineGallery: {
      notifications: {
        clearError: 'No se pudo eliminar la evidencia sin conexión',
        clearSuccess: 'La evidencia sin conexión se eliminó correctamente',
      },
      title: '{{evidenceLower}} sin conexión',
      tooltips: {
        cannotSyncWhileOffline: 'No se puede sincronizar sin conexión',
        clear: 'Eliminar evidencia sin conexión',
        sync: 'Sincronizar evidencia sin conexión',
        syncInProgress: 'Sincronización en curso...',
      },
    },
    sampleBreakdown: {
      labels: {
        aiBreakdown: 'Desglose de IA',
        manualBreakdown: 'Desglose manual',
        noNotes: 'Sin notas',
        sample: 'Muestra',
        sampleEvidence: 'Evidencia de la muestra',
      },
      totalWeight: '{{totalWeight}}kg',
    },
    sampleFormFields: {
      breakdowns: {
        alu: {
          groups: {
            aluminium: 'Aluminio',
            nonAluminium: 'No aluminio',
          },
          items: {
            foilAndTray: 'Lámina y bandeja',
            foodCan: 'Lata para alimentos',
            nonMetal: 'No metálico',
            otherAluminium: 'Otro aluminio',
            otherAluminiumAerosols: 'Otro aluminio (aerosoles)',
            otherMetal: 'Otro metal',
            rigidPlastics: 'Plásticos rígidos',
            ubc: 'UBC',
            vapes: 'Vaporizadores',
          },
        },
        pet: {
          groups: {
            otherPetAndPlastics: 'Otros PET y plásticos',
            petBottles: 'Botellas PET',
          },
          items: {
            clear: 'Transparente',
            clearFullSleeve: 'Transparente (manga completa)',
            clearLarge: 'Transparente (grande)',
            clearPetTray: 'Bandeja PET transparente',
            darkBlue: 'Azul oscuro',
            film: 'Film',
            green: 'Verde',
            lightBlue: 'Azul claro',
            liquid: 'Líquido',
            nonFoodGradeBottle: 'Botella PET no apta para alimentos',
            nonPlastic: 'No plástico',
            other: 'Otros',
            otherPlastic: 'Otro plástico',
            prohibitive: 'Prohibitivo',
          },
        },
        taintTabor: {
          groups: {
            aluWithAttachments: 'Alu. con adherencias',
            aluminium: 'Aluminio',
            nonAluminium: 'No aluminio',
          },
          items: {
            aluIron: 'Alu./hierro',
            aluNonMetal: 'Alu./no metálico',
            cast: 'Fundición',
            castIron: 'Fundición/hierro',
            ferrousMetal: 'Metal ferroso',
            finesLt20mm: 'Finos <20 mm',
            nonMetal: 'No metálico',
            otherMetal: 'Otro metal',
            prohibitive: 'Prohibitivo',
            radiatorCondenser: 'Radiador/condensador',
            stainlessSteel: 'Acero inoxidable',
            taintTabor: 'Taint Tabor',
            ubcFoil: 'UBC/lámina',
          },
        },
        zorba: {
          groups: {
            nonTarget: 'No objetivo',
            targetMaterial: 'Material objetivo',
          },
          items: {
            aluminiumFoilUbc: 'Aluminio - lámina / UBC',
            compositeMaterials: 'Materiales compuestos',
            copperAndBrass: "Cobre y latón 'rojos y amarillos'",
            finesLt20mm: 'Finos <20 mm',
            nonFerrousMetal: "Metal no ferroso 'blancos'",
            otherMetalFerrous: 'Otro metal (ferroso)',
            otherNonMetal: 'Otro no metálico',
            postConsumerAluminiumPackaging: 'Envases de aluminio posconsumo',
            prohibitive: 'Prohibitivo',
          },
        },
      },
      labels: {
        notes: 'Notas',
        totalWeight: 'Peso total',
        weightUnit: 'kg',
      },
    },
    sampleMaterialEditForm: {
      actions: {
        edit: 'Editar',
      },
      warning: {
        editMessage:
          'Estás a punto de cambiar esta evidencia. Esto la eliminará del informe de calidad asociado. Confirma para continuar.',
        title: '¡Advertencia!',
      },
    },
    sampleSessionDetails: {
      manualSampleAnalysisTitle: 'Análisis manual de la muestra',
      noData: 'Sin datos',
      sampleImagesTitle: 'Imágenes de muestra',
    },
    sessionStats: {
      excludedSubtitle: 'Las imágenes no cumplieron nuestras reglas de requisitos',
      excludedTitle: 'Excluidas',
      includedSubtitle: 'Las imágenes cumplieron nuestras reglas de requisitos',
      includedTitle: 'Incluidas',
    },
    unloadingPhotoUploader: {
      label: 'Subir fotos',
      notifications: {
        uploadFailed: 'Error al cargar',
        uploadSuccess: 'Carga completada',
      },
    },
  },
  helpGuides: {
    aiBale: {
      partTwo: {
        captions: {
          clearWhichBaleToFocusOn: 'Se ve claramente en qué bala enfocarse',
          goodQualityPhoto: 'Foto de buena calidad',
          goodQualityPhotoAndDistanceFromBale: 'Buena calidad de foto y distancia respecto a la bala',
          greatDistanceFromBale: 'Muy buena distancia respecto a la bala',
          materialNotTooTightlyPacked: 'El material no está demasiado compactado',
          materialTooFlatAndTightlyPacked: 'El material está demasiado plano y compactado',
          nearlyAllBaleFaceShown: 'Se muestra casi toda la cara de la bala',
          photoTooBlurry: 'La foto está demasiado borrosa',
          someBackgroundIsOk: 'Algo de fondo está bien',
          tooClose: 'Demasiado cerca',
          tooManyBalesAngleNotStraightPoorQuality: 'Demasiadas balas, ángulo no frontal y mala calidad',
          tooManyBalesManySidesVisible: 'Demasiadas balas, se ven muchos lados',
          tooManyBalesTooFarAway: 'Demasiadas balas, demasiado lejos',
          unclearWhichIsMainBale: 'No está claro cuál es la bala principal',
        },
        intro: 'Aquí tienes ejemplos de fotos buenas y malas:',
      },
    },
    aiBriquette: {
      partOne: {
        imageAlt: 'Ponte a un máximo de 0,5 metros',
        instructions: '<bold>Ponte a 0,5 metros</bold> delante de la briqueta.',
      },
      partTwo: {
        analysisInstruction: 'Al analizar un palé, <bold>haz fotos de al menos 6 briquetas.</bold>',
        captions: {
          goodDistance: 'Buena distancia',
          markingsVisible: 'Marcas visibles',
          noMarkingsVisible: 'Sin marcas visibles',
          okDistance: 'Distancia aceptable',
          tooDirty: 'Demasiado sucio',
          tooShredded: 'Demasiado triturado',
        },
        intro: 'Aquí tienes ejemplos de fotos buenas y malas:',
      },
    },
    aiLoose: {
      partTwo: {
        captions: {
          cannotSeeAllMaterial: 'No se ve todo el material',
          floorHasLargeMarkings: 'El suelo tiene marcas grandes',
          materialInsideBlackBox: 'El material está dentro del recuadro negro',
          materialNotOnGroundOrTable: 'El material no está en el suelo ni en una mesa',
          materialShouldNotTouchPurpleLines: 'El material no debe tocar las líneas moradas',
          materialSpreadVeryWell: 'El material está muy bien extendido',
          materialSpreadWell: 'El material está bien extendido',
          materialTouchesEdge: 'El material toca el borde',
          shadow: 'Sombra',
          squareFullOfMaterial: 'El cuadrado está lleno de material',
          tooHeapedAndTooBig: 'Demasiado amontonado y demasiado grande',
        },
        intro: 'Aquí tienes ejemplos de fotos buenas y malas:',
      },
    },
    alert: {
      actions: {
        startGuide: 'Iniciar guía - 1 min',
      },
      description: {
        obfuscatedAi: 'Lee esta guía corta para tomar las fotos correctas.',
        withAi:
          'Lee esta guía corta para tomar las fotos correctas y obtener resultados de IA precisos. Fotos malas = resultados malos.',
      },
      title: '¿Primera vez usando {{modelTitle}}?',
    },
    autoCaptureTip: {
      message:
        'Consejo: ve más rápido y mantén los guantes puestos con <autoCapture>Captura automática</autoCapture>, que toma las fotos por ti.',
    },
    common: {
      actions: {
        back: 'Atrás',
        finish: 'Finalizar',
        next: 'Siguiente',
        skipGuide: 'Saltar guía',
      },
      models: {
        bale: 'bala',
        briquette: 'briqueta',
      },
    },
    complete: {
      message:
        "Cuando termines, pulsa <complete>'Completar'</complete> en la cámara. Después verás el <qualityReport>informe de calidad.</qualityReport>",
    },
    dialog: {
      stepLabel: 'Paso {{currentStep}}/{{maxSteps}}',
      title: '¡Bienvenido a {{modelTitle}}!',
    },
    guideTitle: {
      obfuscatedAi: 'Aprende a tomar fotos correctamente para obtener los mejores resultados.',
      withAi: 'Aprende a tomar fotos correctamente para obtener los mejores resultados de IA.',
    },
    offlineWarning: {
      message:
        'Consejo: activa el <offlineMode>modo sin conexión</offlineMode> para no perder fotos cuando la conexión de datos sea débil.',
    },
    reviewDeleteHelpText: {
      withAi:
        'Puedes ver si la IA ha <accepted>aceptado</accepted> o <rejected>rechazado</rejected> un {{model}}. <delete>Elimina</delete> y <retake>vuelve a tomar</retake> las fotos incorrectas.',
      withoutAi:
        'Puedes ver si un {{model}} ha sido <accepted>aceptado</accepted> o <rejected>rechazado</rejected>. <delete>Elimina</delete> y <retake>vuelve a tomar</retake> las fotos incorrectas.',
    },
    reviewDeleteTip: {
      message:
        'Consejo útil: también puedes <reviewDelete>revisar y eliminar</reviewDelete> y <addMorePhotos>añadir más fotos</addMorePhotos> en el Informe de calidad.',
    },
  },
  instructions: {
    photoInstructionsDrawer: {
      actions: {
        doNotShowAgain: 'No volver a mostrar',
        takePhoto: 'Tomar foto',
      },
      description:
        'Nuestra IA analiza las imágenes subidas según múltiples parámetros. Tus imágenes subidas deben parecerse a las que aparecen a continuación.',
      frontFaceDescription:
        'La causa más común de una imagen no válida es la orientación de la bala. Los objetos de la bala deben estar aplanados y de frente.',
      frontFaceTitle: '¿Qué es la cara frontal?',
      imageAlt: {
        balePhotoExample: 'Ejemplo de foto de bala',
        frontFaceBalePhoto: 'Foto de bala con cara frontal',
        treeRingBalePhoto: 'Foto de bala con anillos de árbol',
      },
      labels: {
        objectsFlattenedFaceOn: 'Objetos aplanados y de frente',
        objectsSquashedSideOn: 'Objetos aplastados y de lado',
      },
      title: 'Tomar imágenes',
    },
  },
  materialComponents: {
    supportedComponent: {
      labels: {
        materialComponent: 'Componente del material',
      },
    },
    supportedComponentValue: {
      adornments: {
        max: 'Máx.: ',
        min: 'Mín.: ',
      },
      labels: {
        booleanValue: 'Valor booleano',
        value: 'Valor',
      },
    },
  },
  navigation: {
    sidebar: {
      closeAriaLabel: 'Cerrar',
    },
    userMenu: {
      language: 'Idioma',
      logOut: 'Cerrar sesión',
      manageCompany: 'Gestionar empresa',
      notifications: 'Configuración de notificaciones',
      qualityDigest: 'Correo diario de calidad',
    },
  },
  networkStatus: {
    alert: {
      offlineMessage:
        'Actualmente estás sin conexión. La evidencia se almacenará localmente hasta que vuelvas a estar en línea.',
    },
    override: {
      label: 'Modo sin conexión',
      notifications: {
        disabled: 'Modo sin conexión desactivado',
        enabled: 'Modo sin conexión activado',
      },
    },
  },
  omnisearch: {
    noOptions: {
      withValue: 'No se encontraron resultados',
      withoutValue: 'Escribe para buscar',
    },
    placeholder: {
      desktop: 'Ctrl/Cmd + K para buscar',
      mobile: 'Buscar',
    },
  },
  qualityReport: {
    footer: {
      backToSiteQualityPage: 'Volver a la página de calidad del sitio',
    },
    link: {
      buttonLabel: 'Informe de calidad',
      tooltip: {
        noMaterialSelected: 'No se ha seleccionado ningún material',
        notEnoughEvidence: 'No hay suficiente evidencia para el material seleccionado',
      },
    },
    page: {
      notAvailable: 'Informe de calidad no disponible',
    },
    sampleControls: {
      actions: {
        viewData: 'Ver datos',
      },
    },
    sampleSelect: {
      label: 'Fecha de inspección',
    },
    share: {
      content: 'Aquí tienes el enlace al informe de calidad de {{brandName}} {{url}}',
      labels: {
        copyLink: 'Copiar enlace',
        copyNotification: 'Copiado al portapapeles',
        download: 'Descargar',
        email: 'Correo electrónico',
        systemShare: 'Compartir con el sistema',
        trigger: 'Compartir',
        triggerAriaLabel: 'Compartir',
        whatsapp: 'WhatsApp',
      },
      subject: 'Informe de calidad de {{brandName}}',
    },
  },
  siteMaterial: {
    tradeCompanySiteMaterialPicker: {
      actions: {
        addNewMaterial: 'Añadir nuevo material',
        addNewMaterialAriaLabel: 'Añadir nuevo material',
      },
      labels: {
        company: 'Empresa',
        siteMaterial: 'Material del sitio',
      },
    },
  },
} satisfies I18nDictionaryShape<typeof featuresEnGb>;
